import { cn } from '@/lib/cn';
import type { CompareImages } from './types';
import type { SliderState } from './useSliderPosition';

interface SliderViewProps extends CompareImages {
  slider: SliderState;
  fullscreen?: boolean;
}

/** Render underneath, original clipped on top; drag the divider to reveal either side. */
export function SliderView({ originalImage, renderImage, slider, fullscreen = false }: SliderViewProps) {
  const { position, handlers } = slider;
  const badge = fullscreen ? 'top-4 px-3 py-1.5 rounded-lg' : 'top-3 px-2.5 py-1 rounded-md';
  const [leftEdge, rightEdge] = fullscreen ? ['left-4', 'right-4'] : ['left-3', 'right-3'];

  return (
    <div
      {...handlers}
      role="slider"
      tabIndex={0}
      aria-label="Before and after comparison"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(position)}
      className={cn(
        'relative w-full overflow-hidden cursor-ew-resize flex items-center justify-center select-none touch-pan-y focus-visible:ring-2 focus-visible:ring-blue-500 outline-none',
        fullscreen ? 'h-full bg-slate-950 rounded-xl' : 'h-[450px]',
      )}
    >
      <img
        src={renderImage}
        alt="After makeover render"
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        referrerPolicy="no-referrer"
      />
      <img
        src={originalImage}
        alt="Before: raw property photo"
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        referrerPolicy="no-referrer"
      />

      <div
        className="absolute top-0 bottom-0 w-1 bg-blue-500 shadow-xl flex items-center justify-center z-20 -translate-x-1/2 pointer-events-none"
        style={{ left: `${position}%` }}
      >
        <div
          className={cn(
            'shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg border-2 border-white font-bold font-mono',
            fullscreen ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs',
          )}
        >
          ↔
        </div>
      </div>

      <div
        className={cn(
          'absolute bg-slate-950/80 backdrop-blur-sm text-slate-200 text-xs font-bold border border-slate-700 pointer-events-none',
          leftEdge,
          badge,
        )}
      >
        BEFORE (RAW PHOTO)
      </div>
      <div
        className={cn(
          'absolute bg-blue-600 text-white text-xs font-bold shadow-md pointer-events-none',
          rightEdge,
          badge,
        )}
      >
        AFTER (MAKEOVER RENDER)
      </div>
    </div>
  );
}
